import { TokenCache } from './tokenCache';

export class TitusTokenService {
    public static getTitusTenantIdFromToken(token: string): string | undefined {
        const payload = TitusTokenService.decodeJwtPayload(token);

        const tenantId = payload.T || undefined;
        if (!tenantId || typeof tenantId !== 'string') {
            console.debug('No valid tenantId (T) claim found in Titus token.');
        }

        return tenantId;
    }

    private static decodeJwtPayload(token: string): JwtPayload {
        const parts = token.split('.');
        if (parts.length !== 3) throw new Error('Invalid JWT format.');

        try {
            return JSON.parse(atob(parts[1]));
        } catch (error) {
            console.warn('Failed to decode JWT payload:', error);
            throw error;
        }
    }

    private titusTokenPromise?: Promise<string>;
    private tokenCache: TokenCache;

    constructor(
        private licensingServerUrl: string,
        private authenticationMode: 'Windows' | 'Office',
        storageKey: string,
    ) {
        this.tokenCache = new TokenCache(storageKey);
    }

    /**
     * Returns a Titus token for the specified user.
     *
     * - If a valid (unexpired and user-matching) token is cached, it is returned.
     * - Otherwise, a new token is fetched from the licensing server using either
     *   Entra ID (JWT bearer) or Windows Integrated Authentication, depending on the configured mode.
     *
     * The token is cached in sessionStorage and automatically reused until it expires or the user changes.
     *
     * @param idToken The Entra ID ID token used for bearer authentication (ignored in Windows mode).
     * @param userId  A stable identifier for the current user (e.g., homeAccountId or sid).
     * @throws If there is no cached token an authentication token acquisitation fails.
     */
    public async getToken(idToken: string | undefined, userId: string): Promise<string> {
        const cachedToken = this.getValidCachedToken(userId);
        if (cachedToken) {
            return cachedToken;
        }

        if (!this.titusTokenPromise) {
            this.titusTokenPromise = this.acquireNewToken(idToken, userId).finally(() => {
                this.titusTokenPromise = undefined;
            });
        }

        return this.titusTokenPromise;
    }

    /**
     * Returns a Titus token for the specified user or the current session.
     *
     * - If a valid (unexpired) token is cached, it is returned.
     * - If a userId is provided, the cached token is only reused if it matches that user.
     * - If no userId is provided, the cached token is reused regardless of user matching.
     *
     * The token is cached in sessionStorage and automatically reused until it expires or
     * a different userId is provided.
     *
     * @param userId  An optional stable identifier for the current user (e.g., homeAccountId or sid).
     * @throws If there is no cached token.
     */
    public getCachedToken(userId?: string): string {
        const cachedToken = this.getValidCachedToken(userId);
        if (cachedToken) {
            return cachedToken;
        }

        throw new Error('No current session token available.');
    }

    private async acquireNewToken(idToken: string | undefined, userId: string): Promise<string> {
        console.info(`Fetching Titus token (${this.authenticationMode} mode)...`);

        const token =
            this.authenticationMode === 'Windows'
                ? await this.requestTokenWithWindowsAuth()
                : await this.requestTokenWithEntraAuth(idToken);

        this.cacheToken(token, userId);
        return token;
    }

    private async requestTokenWithWindowsAuth(): Promise<string> {
        const requestOptions: RequestInit = {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        };

        return this.makeTokenRequest(requestOptions);
    }

    private async requestTokenWithEntraAuth(idToken: string | undefined): Promise<string> {
        if (!idToken) {
            throw new Error('Missing user identity token (ID token).');
        }

        const requestOptions: RequestInit = {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        };

        return this.makeTokenRequest(requestOptions);
    }

    private async makeTokenRequest(requestOptions: RequestInit): Promise<string> {
        try {
            const result = await fetch(this.licensingServerUrl, requestOptions);

            if (!result.ok) {
                throw new Error(`Licensing server error: HTTP ${result.status} (${result.statusText})`);
            }

            const data = await result.json();
            if (!data.titusToken) {
                throw new Error('Missing Titus token in response');
            }
            return data.titusToken;
        } catch (error) {
            console.error('Error requesting Titus token:', error);
            throw error;
        }
    }

    // will log and silently fail if token parsing or storage interface fails
    private cacheToken(token: string, userId: string): void {
        try {
            const payload = TitusTokenService.decodeJwtPayload(token);

            let expiry = payload.exp;

            if (!expiry || typeof expiry !== 'number') {
                console.debug('No valid expiry (exp) claim found in Titus token. Assuming 30 mins.');
                const currentUnixTime = Math.floor(Date.now() / 1000);
                expiry = currentUnixTime + 30 * 60;
            }

            this.tokenCache.set(token, userId, expiry);
        } catch (error) {
            console.warn('Error decoding token to retrieve claims.', error);
        }
    }

    private getValidCachedToken(userId?: string): string | null {
        return this.tokenCache.get(userId);
    }
}

interface JwtPayload {
    exp?: number;
    T?: string;
    [key: string]: unknown;
}
